import type React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldValues } from 'react-hook-form';


import { Stack } from 'ui/stack';

import Select from 'components/partials/select/select';

import { useUsersList } from 'hooks/api/use-users';
import type { UserRecord } from 'hooks/api/use-users';

import { mapUsersToSelectOptions } from 'helpers/forms';
import type { SelectOption } from 'helpers/forms';

import { ASIGNED_CONSULTANTS_FORM_FIELDS } from 'containers/admin/clients/client/forms/asigned-consultants-form/asigned-consultants-form-fields.constants';

interface AsignedConsultantsFormProps {
  isFormDisabled?: boolean;
  control: Control<FieldValues>;
}

const AsignedConsultantsForm = ({
  isFormDisabled,
  control,
}: AsignedConsultantsFormProps): React.JSX.Element => {
  const { data: superAdmins = [] } = useUsersList({
    pageSize: 9999,
    filters: {
      authority: [
        'ROLE_SUPER_ADMIN',
        'ROLE_FD_PRINCIPAL',
        'ROLE_FD_CLIENT_SERVICE',
        'ROLE_FD_ANALYST',
        'ROLE_INTERNAL_USER',
        'ROLE_REPORT_ADMIN',
      ],
    },
  });

  const getConsultants = (
    optionsKey: string
  ): (SelectOption | undefined)[] | undefined => {
    const currentConsultants = (superAdmins as UserRecord[]).filter(
      (user) => user.superAdmin || user[optionsKey]
    );

    return mapUsersToSelectOptions(currentConsultants as never);
  };

  return (
    <Stack sx={{ width: '50%' }}>
      {ASIGNED_CONSULTANTS_FORM_FIELDS.map(
        ({ label, name, key, renderComponentProps, optionsKey }) => (
          <Controller
            key={key}
            name={name}
            control={control}
            render={({ field }) => (
              <Select<SelectOption>
                label={label}
                labelInTop
                options={(getConsultants(optionsKey) ?? []).filter(
                  (opt): opt is SelectOption => Boolean(opt)
                )}
                disabled={isFormDisabled}
                disabledBgColor={isFormDisabled ? '#F2F2F2' : undefined}
                {...field}
                value={field.value as SelectOption | SelectOption[] | undefined}
                {...(renderComponentProps as Record<string, unknown>)}
              />
            )}
          />
        )
      )}
    </Stack>
  );
};

export default AsignedConsultantsForm;
